import { betaSlugToMapId } from "@dailywars/core/engine";
import { createFileRoute, notFound } from "@tanstack/react-router";

import { useEndTurnConfirmation } from "#/components/game/useEndTurnConfirmation";
import { PlayMapAccessGate } from "#/components/play/PlayMapAccessUpsell";
import { PlayMapPageShell } from "#/components/play/PlayMapPageShell";
import { playSessionConfig } from "#/components/play/playSession";
import {
  useAiPlaybackStepTimer,
  useEscapeCancelsSelection,
  usePlayMapBoardChrome,
  usePlayMapGameHotkeys,
  usePlayMapTurnActions,
  usePlayMapUndo,
  useVictoryAutoSubmitTurn,
} from "#/components/play/usePlayMapSession";
import { BoardImagesGate } from "#/game/BoardImagesGate";
import { GameStoreProvider, useGameSelector } from "#/game/GameStoreProvider";
import { readAnonymousPlayerId, syncAnonymousPlayerIdOnClient } from "#/lib/anonymousPlayerId";
import { betaMapDescription, betaMapRobots, betaMapTitle, documentHead } from "#/lib/documentTitle";
import { gameSettingsForSession } from "#/lib/guestGameplaySettings";
import { isMapAccessUpsellReason } from "#/lib/mapAccessUpsellCopy";
import { isRegisteredPlayerId } from "#/lib/playerDisplayName";
import { betaMapSlugRouteParams } from "#/lib/routeParams";
import { useRequestGameContext } from "#/lib/useRequestGameContext";
import { loadBetaMapPage, type PlayMapPageReady } from "#/server/game/playMapPage";

function readPlayerId(): string | undefined {
  return readAnonymousPlayerId();
}

export const Route = createFileRoute("/beta_/$slug")({
  params: betaMapSlugRouteParams,
  loader: async ({ params: { slug } }) => {
    const mapId = betaSlugToMapId(slug);
    const playerId = readPlayerId();
    const result = await loadBetaMapPage({
      data: playerId ? { mapId, playerId } : { mapId },
    });
    if (result.status === "access-denied" && result.reason === "invalid-id") {
      throw notFound();
    }
    if (playerId && result.status !== "access-denied") {
      syncAnonymousPlayerIdOnClient({
        session: {
          playerId: result.playerId,
          isAnonymous: !isRegisteredPlayerId(result.playerId),
        },
      });
    }
    return result;
  },
  head: ({ params: { slug } }) =>
    documentHead(betaMapTitle(slug), {
      description: betaMapDescription(slug),
      canonicalPath: `/beta/${slug}`,
      robots: betaMapRobots(),
    }),
  component: BetaPlayPage,
});

function BetaPlayPage() {
  const loaderData = Route.useLoaderData();

  if (loaderData.status === "access-denied") {
    if (!isMapAccessUpsellReason(loaderData.reason)) {
      throw notFound();
    }
    return <PlayMapAccessGate reason={loaderData.reason} mapId={loaderData.mapId} />;
  }

  return <BetaPlayReadyPage initial={loaderData} />;
}

function BetaPlayReadyPage({ initial }: { initial: PlayMapPageReady }) {
  const session = useRequestGameContext();
  return (
    <BoardImagesGate state={initial.state} mapId={initial.mapId}>
      <GameStoreProvider
        initialState={initial.state}
        initialServerDayNumber={initial.currentDay}
        settings={gameSettingsForSession(session)}
      >
        <BetaPlaySession initial={initial} />
      </GameStoreProvider>
    </BoardImagesGate>
  );
}

function BetaPlaySession({ initial }: { initial: PlayMapPageReady }) {
  useAiPlaybackStepTimer();
  useEscapeCancelsSelection();
  useVictoryAutoSubmitTurn({ playerId: initial.playerId, mapId: initial.mapId });
  const endTurnConfirmation = useGameSelector((s) => s.endTurnConfirmation);
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const { handleEndTurn, handleRestart, handleUndoCommittedTurn, isRestarting } =
    usePlayMapTurnActions({
      playerId: initial.playerId,
      mapId: initial.mapId,
      routeMapId: initial.mapId,
    });
  const {
    requestEndTurn,
    confirmDialog: endTurnDialog,
    dialogOpen: endTurnDialogOpen,
  } = useEndTurnConfirmation({
    onConfirm: handleEndTurn,
    pending: isSubmitting,
    confirmBeforeEndTurn: endTurnConfirmation,
  });
  const { handleUndo, undoTurnDialog, undoDialogOpen } = usePlayMapUndo({
    onUndoCommittedTurn: handleUndoCommittedTurn,
    pending: isSubmitting,
  });
  const boardDialogOpen = endTurnDialogOpen || undoDialogOpen;

  usePlayMapGameHotkeys({
    onEndTurn: requestEndTurn,
    onUndo: handleUndo,
    paused: boardDialogOpen,
  });

  const chrome = usePlayMapBoardChrome();

  return (
    <>
      <PlayMapPageShell
        handleUndo={handleUndo}
        handleEndTurn={requestEndTurn}
        handleRestart={handleRestart}
        isRestarting={isRestarting}
        boardDialogOpen={boardDialogOpen}
        medalThresholds={null}
        session={playSessionConfig({ kind: "beta", mapId: initial.mapId })}
        {...chrome}
      />
      {endTurnDialog}
      {undoTurnDialog}
    </>
  );
}
